<template>
  <v-select
    :disabled="disabled"
    :options="options"
    :filterable="false"
    :label="label"
    :reduce="reduce"
    :placeholder="placeholder"
    :class="inputClass"
    v-model="selectedValue"
    @open="onOpen"
    @close="onClose"
    @search="onSearch"
    :multiple="isMultiple"
    @update:modelValue="optionSelected"
    ref="dropdownRef"
    :clearable="isClearable"
  >
    <template v-slot:option="option">
      {{ formatLabel(option) }}
    </template>
    <template #list-footer>
      <li v-show="hasNextPage" ref="load" class="loader">
        <template v-if="isLoading">
          Loading more options...
        </template>
        <template v-if="skipApiOnOpen || minCharToSearch > 0">
          Start typing to get options
        </template>
      </li>
    </template>
  </v-select>
</template>

<script setup>
import {computed, inject, nextTick, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {debounce} from "lodash";

  const paginated = ref([])
  const $route = useRoute()
  const emit = defineEmits([
    'update:modelValue',
    'onClear',
    'newAdd'
  ]);
  const props = defineProps({
    additionalOption: {
      type: Array,
      default: []
    },
    modelValue: {
      required: true
    },
    limit: {
      type: Number,
      default: 20,
      required: false
    },
    label: {
      type: String,
      default: "name"
    },
    placeholder: {
      type: String,
      default: "Please search or select"
    },
    searchKey: {
      type: String,
      default: "q"
    },
    urlParam: {
        type: Number,
        default: null
    },
    additionalQuery: {
      type: Object,
      default: {}
    },
    formatLabel: {
      type: Function,
      default: (option) => option.name
    },
    optionSelected: {
      type: Function,
      default: () => {}
    },
    reduce: {
      default: name => name.id
    },
    apiService: {
      required: true
    },
    isMultiple: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    isNewAdded: {
      default: false,
      type: Boolean,
    },
    minCharToSearch: {
      type: Number,
      default: 0,
      required: false
    },
    skipApiOnOpen: {
      type: Boolean,
      default: false
    },
    inputClass: {
      type: String,
      default: ''
    },
    autoSelectFirst: {
      type: Boolean,
      default: false
    },
    isClearable: {
      type: Boolean,
      default: true
    }
  })
  const observer = new IntersectionObserver(entries => infiniteScroll(entries))
  const page = ref(0)
  const load = ref(null)
  const dropdownRef = ref(null)
  const query = ref('')
  const showError = inject('showError');
  const hasNextPage = ref(true);
  const customOption = ref(null);
  const isDropdownOpen = ref(false)
  const skipAPICall = ref(false)
  const isLoading = ref(false)
  let position = 0;

  const getQuery = (firstPage = false) => {
    const additionalQuery = props.additionalQuery;
    let companyQuery = `?company_id=${$route.params.companyId}&page=${!firstPage ? page.value: 1}&per_page=${props.limit}`;

    if (additionalQuery && Object.keys(additionalQuery).length > 0) {
      let params = new URLSearchParams(props.additionalQuery);
      companyQuery += `&${params.toString()}`
    }

    if (query.value && query.value !== '') {
      companyQuery += `&${props.searchKey}=${query.value}`
    }

    return companyQuery;
  }

  const selectedValue = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value)
  })

  const options = computed(() => {
    let base = props.additionalOption
      ? [...props.additionalOption, ...paginated.value]
      : [...paginated.value];

    if (customOption.value) {
      base.unshift(customOption.value);
    }

    return base;
  });

  //watcher
  watch(page, (newValue) => {
    if (newValue && !skipAPICall.value) {
      handleAPICall(getQuery(), false)
    }
  })

  watch(query, () => {
    if (! isDropdownOpen.value) {
      return;
    }

    if (props.minCharToSearch > 0 && query.value.length < props.minCharToSearch) {
      return;
    }

    handleAPICall(getQuery(true))
  })

  watch(selectedValue, (newValue, oldValue) => {
    if (oldValue !== null && newValue === null) {
      emit('onClear')
      query.value = '';
    }

    if (props.isNewAdded) {
      if (props.isMultiple && Array.isArray(newValue)) {
        const lastSelected = newValue[newValue.length - 1];
        if (customOption.value && lastSelected.name === customOption.value.name) {
          emit('newAdd', customOption.value);
          customOption.value = null;
        }
      }
      
      if (!props.isMultiple && customOption.value && newValue == customOption.value.name) {
        emit('newAdd', customOption.value);
        customOption.value = null;
      }
    }
  })

  const onSearch = debounce((search) => {
    position = 0;
    query.value = search;
  }, 350);

  const clear = () => {
    paginated.value = [];
    page.value = 0;
    hasNextPage.value = true;
  };

  const onOpen = async () => {
    isDropdownOpen.value = true;
    if (query.value !== '' && query.value.length && !selectedValue.value) {
      query.value = '';
    }

    if (props.skipApiOnOpen) {
      return;
    }

    if (hasNextPage.value) {
      await nextTick()
      observer.observe(load.value)
    }
  }
  const onClose = () => {
    isDropdownOpen.value = false;
    observer.disconnect()
  }

  const callApi = (companyQuery) => {
    if (! props.urlParam) {
      return props.apiService(companyQuery);
    }

    return props.apiService(props.urlParam, companyQuery);
  }

  const handleAPICall = (companyQuery, refreshOption = true) => {
    isLoading.value = true;
    callApi(companyQuery).then(res => {
      if (! res.data) {
        return;
      }

      skipAPICall.value = true;
      page.value = res.data.current_page ?? 1;
      hasNextPage.value = res.data.current_page !== res.data.last_page;

      if(props.isNewAdded) {
        const results = res.data.data;
        const searchText = query.value.trim().toLowerCase();
        const exists = results.some(opt => {
          const label = opt[props.label] && typeof opt[props.label] === 'string'
                        ? opt[props.label].toLowerCase()
                        : '';
          return label === searchText;
        });

        customOption.value = !exists && searchText
        ? {
            id: `custom_${searchText}`,
            name: query.value,
            is_custom: true,
          }
        : null;
      }

      if (props.autoSelectFirst && res.data.data.length > 0 && !selectedValue.value) {
        selectedValue.value = res.data.data[0];
      }

      if (refreshOption) {
        paginated.value = res.data.data
        return;
      }

      paginated.value = [
        ...paginated.value,
        ...res.data.data
      ];
    }).finally(() => {
      isLoading.value = false;
      skipAPICall.value = false;
    })
  }

  const infiniteScroll = async ([{ isIntersecting, target }]) => {
    if (!isIntersecting) {
      return;
    }

    page.value++;
    await nextTick();
    if(target) {
      position = target.offsetParent.scrollTop;
    }
  }

onMounted(async () => {
  if (props.autoSelectFirst) {
    await handleAPICall(getQuery(true));
  }
});

  defineExpose({
      dropdownRef,
      clear
  })
</script>
